import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import DatePicker from "react-datepicker";
import {
  availableGenres,
  convertToUTC,
  getPublicImageUrl,
} from "../helpers/helpers";
import { v4 as uuidv4 } from "uuid";
import SuccessPopup from "../components/SuccessPopup";
import { Table } from "flowbite-react";
import { supabase } from "../supabaseClient";
import dayjs from "dayjs";
import SpinWinTab from "../components/SpinWinTab";
import SpinWinAnalytics from "../components/SpinWinAnalytics";

export default function ManageEventDetail() {
  const { id } = useParams();

  const [event, setEvent] = useState();

  const navigate = useNavigate();

  // Can be details or attendees
  const [activeTab, setActiveTab] = useState("details");
  const [error, setError] = useState([]);
  const [venues, setVenues] = useState([]);
  const [success, setSuccess] = useState(false);
  const [cities, setCities] = useState([]);
  const [signups, setSignups] = useState([]);

  useEffect(() => {
    supabase
      .from("EventSignUp")
      .select("*, event:Events (*)")
      .eq("event_id", id)
      .then((res) => {
        console.log(res.data);
        setSignups(res.data);
      });
  }, [id]);

  useEffect(() => {
    supabase
      .from("Events")
      .select("*, attendees:EventSignUp(*)")
      .eq("id", id)
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
        } else {
          setEvent({ ...data[0], genres: data[0].genres.split(", ") });
        }
      });
  }, [id]);

  useEffect(() => {
    supabase
      .from("Venue")
      .select("*")
      .then((res) => {
        setVenues(res.data);
      });

    supabase
      .from("City")
      .select("*")
      .then((res) => {
        setCities(res.data);
      });
  }, []);

  const saveEvent = () => {
    const optionalFields = [
      "coupon_code",
      "artists",
      "sold_out",
      "trending",
      "exclusive_event",
      "commissions",
      "has_vip_table",
      "vip_table_link",
      "plus_one_invites_enabled",
    ];

    // Guestlists dont have a ticket link
    if (event.category === "Guestlist") {
      optionalFields.push("ticket_link");
    }

    // Error handling
    let hasError = false;
    Object.values(event).forEach((item, index) => {
      if (item === "" && !optionalFields.includes(Object.keys(event)[index])) {
        setError((prev) => {
          return [...prev, Object.keys(event)[index]];
        });
        hasError = true;
      } else {
        setError((prev) => {
          return prev.filter((error) => error !== Object.keys(event)[index]);
        });
      }
    });

    if (hasError) {
      return;
    }

    delete event["venue"];
    delete event["attendees"];

    // Convert the time to UTC before sending to the server
    const utcDateTime = convertToUTC(event.datetime);
    const utcSignupFrom = convertToUTC(event.signup_from);
    const utcSignupUntil = convertToUTC(event.signup_until);

    // Create a new object with the UTC datetime
    const eventToSave = {
      ...event,
      datetime: utcDateTime,
      signup_from: utcSignupFrom,
      signup_until: utcSignupUntil,
    };

    supabase
      .from("Events")
      .update({ ...eventToSave, genres: event.genres.join(", ") })
      .eq("id", event.id)
      .then((response) => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 2000);
      });
  };

  const uploadImage = (file) => {
    const uuid = uuidv4();

    const asset_name = `${uuid}-${file.name}`;

    supabase.storage.from("images").upload(asset_name, file, { upsert: true });

    setEvent({ ...event, event_image_filename: asset_name });
  };

  const addOrRemoveGenre = (genre) => {
    if (!event) return;

    if (event.genres.includes(genre)) {
      const genreCopy = [...event.genres];

      const i = event.genres.indexOf(genre);
      genreCopy.splice(i, 1);
      // Remove genre
      setEvent({ ...event, genres: genreCopy });
    } else {
      // Add genre
      setEvent({ ...event, genres: [...event.genres, genre] });
    }
  };

  const validateAndUploadImage = (e) => {
    const file = e.target.files[0];

    if (file) {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        if (img.width === 650 && img.height === 350) {
          // Proceed with upload if the dimensions are correct
          uploadImage(file);
        } else {
          setError((prev) => {
            return [...prev, "event_image_filename"];
          });
          alert(
            `Image must be 650px by 350px! The image you uploaded is ${img.width}px by ${img.height}px.`
          );
        }

        URL.revokeObjectURL(objectUrl); // Clean up the object URL
      };

      img.src = objectUrl;
    }
  };

  const formatDateTime = (datetime) => {
    return dayjs(datetime).local("nl").format("D MMMM YYYY");
  };

  useEffect(() => {
    console.log("formData", event?.datetime);
  }, [event]);

  const addCommission = () => {
    const currentCommissions = event.commissions
      ? event.commissions.split(",").map((c) => c.trim())
      : [];
    setEvent({
      ...event,
      commissions: [...currentCommissions, ""].join(", "),
    });
  };

  const removeCommission = (index) => {
    const commissionArray = event.commissions.split(",").map((c) => c.trim());
    commissionArray.splice(index, 1);
    setEvent({
      ...event,
      commissions: commissionArray.join(", "),
    });
  };

  const updateCommission = (index, value) => {
    const commissionArray = event.commissions
      ? event.commissions.split(",").map((c) => c.trim())
      : [];
    // Clean input to only allow numbers and dots
    value = value.replace(/[^0-9.]/g, "");
    commissionArray[index] = value;
    setEvent({
      ...event,
      commissions: commissionArray.join(", "),
    });
  };

  const fetchEvent = () => {
    supabase
      .from("Events")
      .select("*, attendees:EventSignUp(*)")
      .eq("id", id)
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
        } else {
          setEvent({ ...data[0], genres: data[0].genres.split(", ") });
        }
      });
  };

  return (
    <>
      {event && (
        <div className="px-[15px] lg:px-[100px] py-[50px]">
          <div className="flex justify-between items-center w-full">
            <div className="flex justify-start items-center gap-5">
              <button
                onClick={() => navigate(-1)}
                className="hover:bg-white/20 p-2 rounded-lg"
              >
                <Icon
                  icon="heroicons:arrow-long-left"
                  className="w-12 h-12 text-white"
                />
              </button>
              <h1 className="w-fit font-semibold text-white !text-5xl !text-start">
                {event?.name}
              </h1>
            </div>
          </div>
          <div className="flex justify-start items-center gap-3 mt-12 text-[18px]">
            <button
              onClick={() => setActiveTab("details")}
              className={`border-b-2 p-3 text-white ${
                activeTab === "details" ? "border-white" : "border-transparent"
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab("attendees")}
              className={`border-b-2 p-3 text-white ${
                activeTab === "attendees"
                  ? "border-white"
                  : "border-transparent"
              }`}
            >
              Attendees ({event?.attendees?.length})
            </button>
            {event.is_spin_win && (
              <>
                <button
                  onClick={() => setActiveTab("spinwin")}
                  className={`border-b-2 p-3 text-white ${
                    activeTab === "spinwin"
                      ? "border-white"
                      : "border-transparent"
                  }`}
                >
                  Spin & Win
                </button>
                <button
                  onClick={() => setActiveTab("spinwinanalytics")}
                  className={`border-b-2 p-3 text-white ${
                    activeTab === "spinwinanalytics"
                      ? "border-white"
                      : "border-transparent"
                  }`}
                >
                  Spin & Win Attendees
                </button>
              </>
            )}
          </div>
          {activeTab === "spinwinanalytics" && (
            <SpinWinAnalytics event={event} />
          )}
          {activeTab === "details" && (
            <div className="mt-12">
              <h2 className="font-semibold text-[25px] text-white">Details</h2>
              <div className="">
                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Event name
                  </label>
                  <input
                    placeholder="Event name"
                    value={event.name}
                    type="text"
                    className={`dark-input ${
                      error.includes("name") ? "border-red-500" : ""
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, name: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Spin & Win Event
                  </label>
                  <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 shadow-lg mt-10 p-6 border border-purple-500/30 rounded-xl">
                    <div className="flex items-center gap-4">
                      <div className="bg-purple-600 p-3 rounded-full">
                        <Icon
                          icon="heroicons:sparkles"
                          className="w-8 h-8 text-white"
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-[24px] text-white">
                          Spin & Win Feature
                        </h3>
                        <p className="text-[16px] text-purple-200">
                          Enable special prize giveaways for this event
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                      <div>
                        <p className="text-[14px] text-white">
                          When enabled, users can spin a wheel to win prizes for
                          this event. You'll be able to configure prizes in the
                          Spin & Win tab after saving.
                        </p>
                      </div>
                      <label className="inline-flex relative items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={event.is_spin_win}
                          className="sr-only peer"
                          onChange={(e) => {
                            setEvent({
                              ...event,
                              is_spin_win: e.target.checked,
                            });
                          }}
                        />
                        <div className="peer after:top-[5px] after:left-[2px] after:absolute bg-gray-800 after:bg-white peer-checked:bg-purple-600 rounded-full after:rounded-full peer-focus:ring-2 peer-focus:ring-purple-500 w-12 after:w-5 h-6 after:h-5 after:content-[''] after:transition-all peer-checked:after:translate-x-6"></div>
                        <span className="ml-3 font-medium text-[16px] text-white">
                          {event.is_spin_win ? "Enabled" : "Disabled"}
                        </span>
                      </label>
                    </div>

                    {event.is_spin_win && (
                      <div className="bg-purple-900/30 mt-4 p-4 border border-purple-500/30 rounded-lg">
                        <p className="text-[14px] text-purple-200">
                          <Icon
                            icon="heroicons:information-circle"
                            className="inline-block mr-1 w-5 h-5"
                          />
                          After saving, you'll be able to configure prizes,
                          eligibility criteria, and more in the Spin & Win tab.
                        </p>
                      </div>
                    )}

                    {event.is_spin_win && (
                      <div className="flex justify-between items-center mt-4 pt-4 border-purple-500/30 border-t">
                        <div>
                          <h4 className="font-bold text-[18px] text-white">
                            Enable +1 Invites
                          </h4>
                          <p className="text-[14px] text-purple-200">
                            Allow users to invite a +1 when they win main prizes. Disable this to prevent users from sharing tickets.
                          </p>
                        </div>
                        <label className="inline-flex relative items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={event.plus_one_invites_enabled !== false}
                            className="sr-only peer"
                            onChange={(e) => {
                              setEvent({
                                ...event,
                                plus_one_invites_enabled: e.target.checked,
                              });
                            }}
                          />
                          <div className="peer after:top-[5px] after:left-[2px] after:absolute bg-gray-800 after:bg-white peer-checked:bg-purple-600 rounded-full after:rounded-full peer-focus:ring-2 peer-focus:ring-purple-500 w-12 after:w-5 h-6 after:h-5 after:content-[''] after:transition-all peer-checked:after:translate-x-6"></div>
                          <span className="ml-3 font-medium text-[16px] text-white">
                            {event.plus_one_invites_enabled !== false ? "Enabled" : "Disabled"}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Event description
                  </label>
                  <textarea
                    value={event.description}
                    placeholder="Event description"
                    rows="5"
                    className={`dark-input ${
                      error.includes("description") ? "border-red-500" : ""
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, description: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Artists (comma separated)
                  </label>
                  <textarea
                    value={event.artists}
                    placeholder="Hardwell, Martin Garrix, Tiesto ..."
                    rows="5"
                    className={`dark-input ${
                      error.includes("artists") ? "border-red-500" : ""
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, artists: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">City</label>
                  <select
                    className={`dark-input ${
                      error.includes("city")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={event.city}
                    onChange={(e) => {
                      setEvent({ ...event, city: e.target.value });
                    }}
                  >
                    <option value="">Choose a city</option>
                    {cities?.map((city) => {
                      return <option value={city.name}>{city.name}</option>;
                    })}
                  </select>
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Date & time
                  </label>
                  <DatePicker
                    selected={event.datetime && new Date(event.datetime)}
                    onChange={(date) => {
                      setEvent({ ...event, datetime: date });
                    }}
                    showTimeSelect
                    timeIntervals={1}
                    dateFormat="d MMMM YYYY HH:mm"
                    className={`dark-input ${
                      error.includes("datetime")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Signup from
                  </label>
                  <DatePicker
                    selected={event.signup_from && new Date(event.signup_from)}
                    onChange={(date) => {
                      setEvent({ ...event, signup_from: date });
                    }}
                    showTimeSelect
                    timeIntervals={1}
                    dateFormat="d MMMM YYYY HH:mm"
                    className={`dark-input ${
                      error.includes("signup_from")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Signup until
                  </label>
                  <DatePicker
                    selected={
                      event.signup_until && new Date(event.signup_until)
                    }
                    onChange={(date) => {
                      setEvent({ ...event, signup_until: date });
                    }}
                    showTimeSelect
                    timeIntervals={1}
                    dateFormat="d MMMM YYYY HH:mm"
                    className={`dark-input ${
                      error.includes("signup_until")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Minimum age
                  </label>
                  <input
                    type="number"
                    className={`dark-input ${
                      error.includes("minimum_age")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={event.minimum_age}
                    onChange={(e) => {
                      setEvent({ ...event, minimum_age: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Event type
                  </label>
                  <select
                    className={`dark-input ${
                      error.includes("event_type")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={event.event_type}
                    onChange={(e) => {
                      setEvent({ ...event, event_type: e.target.value });
                    }}
                  >
                    <option value="">Choose event type</option>
                    <option value="Nacht event">Nacht event</option>
                    <option value="Dag event">Dag event</option>
                    <option value="Festival">Festival</option>
                  </select>
                </div>
                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Category
                  </label>
                  <select
                    className={`dark-input ${
                      error.includes("category")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={event.category}
                    onChange={(e) => {
                      setEvent({ ...event, category: e.target.value });
                    }}
                  >
                    <option value="">Choose event category</option>
                    <option value="Community ticket">Community ticket</option>
                    <option value="Guestlist">Guestlist</option>
                  </select>
                </div>

                {event.category === "Community ticket" && (
                  <div className="flex flex-col justify-start gap-1 mt-6">
                    <label className="mb-0 text-neutral-500 text-xl">
                      Ticket link
                    </label>
                    <input
                      placeholder="Ticket link"
                      value={event.ticket_link}
                      type="text"
                      className={`dark-input ${
                        error.includes("ticket_link")
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      onChange={(e) => {
                        setEvent({ ...event, ticket_link: e.target.value });
                      }}
                    />
                  </div>
                )}

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Trending
                  </label>
                  <input
                    checked={event.trending}
                    type="checkbox"
                    className={`dark-checkbox ${
                      error.includes("trending")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, trending: e.target.checked });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Sold out
                  </label>
                  <input
                    checked={event.sold_out}
                    type="checkbox"
                    className={`dark-checkbox ${
                      error.includes("sold_out")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, sold_out: e.target.checked });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Original price
                  </label>
                  <input
                    value={event.original_price}
                    type="text"
                    placeholder="50"
                    className={`dark-input ${
                      error.includes("original_price")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({
                        ...event,
                        original_price: e.target.value,
                      });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Our price
                  </label>
                  <input
                    value={event.our_price}
                    type="text"
                    placeholder="22,50"
                    className={`dark-input ${
                      error.includes("our_price")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, our_price: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Commissions
                  </label>
                  <div className="flex flex-col gap-3">
                    {event.commissions?.split(",").map((commission, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={commission.trim()}
                          placeholder="15.00"
                          className={`dark-input w-32 ${
                            error.includes("commissions")
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          onChange={(e) =>
                            updateCommission(index, e.target.value)
                          }
                        />
                        <button
                          onClick={() => removeCommission(index)}
                          className="bg-red-500 hover:bg-red-600 p-2 rounded-lg text-white"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addCommission}
                      className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg w-fit text-white"
                    >
                      Add Commission
                    </button>
                  </div>
                  <p className="text-neutral-500 text-sm">
                    Enter commission amounts (e.g. 15.00)
                  </p>
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Currency
                  </label>
                  <select
                    className={`dark-input`}
                    value={event.currency}
                    onChange={(e) => {
                      setEvent({ ...event, currency: e.target.value });
                    }}
                  >
                    <option value="€">€</option>
                    <option value="Rp">Rp</option>
                    <option value="£">£</option>
                  </select>
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Coupon code (optional)
                  </label>
                  <input
                    value={event.coupon_code}
                    type="text"
                    className={`dark-input ${
                      error.includes("coupon_code")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({ ...event, coupon_code: e.target.value });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">Venue</label>
                  <select
                    className={`dark-input ${
                      error.includes("venue_id")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={event.venue_id}
                    onChange={(e) => {
                      setEvent({ ...event, venue_id: e.target.value });
                    }}
                  >
                    <option value="">Choose a venue</option>
                    {venues.map((venue) => {
                      return <option value={venue.id}>{venue.name}</option>;
                    })}
                  </select>
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Exclusive event
                  </label>
                  <input
                    checked={event.exclusive_event}
                    type="checkbox"
                    className={`dark-checkbox ${
                      error.includes("exclusive_event")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      setEvent({
                        ...event,
                        exclusive_event: e.target.checked,
                      });
                    }}
                  />
                </div>

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Has VIP Table
                  </label>
                  <input
                    checked={event.has_vip_table}
                    type="checkbox"
                    className={`text-2xl w-8 h-8 text-black bg-white border-2 ${
                      error.includes("has_vip_table")
                        ? "border-red-500"
                        : "border-gray-300"
                    } p-4 rounded-lg`}
                    onChange={(e) => {
                      setEvent({ ...event, has_vip_table: e.target.checked });
                    }}
                  />
                </div>

                {event.has_vip_table && (
                  <div className="flex flex-col justify-start gap-1 mt-6">
                    <label className="mb-0 text-neutral-500 text-xl">
                      VIP Table Link
                    </label>
                    <input
                      value={event.vip_table_link}
                      type="text"
                      placeholder="https://..."
                      className={`text-2xl text-black bg-white border-2 ${
                        error.includes("vip_table_link")
                          ? "border-red-500"
                          : "border-gray-300"
                      } p-4 rounded-lg`}
                      onChange={(e) => {
                        setEvent({ ...event, vip_table_link: e.target.value });
                      }}
                    />
                  </div>
                )}

                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Genres
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableGenres.map((genre) => {
                      return (
                        <button
                          onClick={() => {
                            addOrRemoveGenre(genre);
                          }}
                          className={`${
                            event.genres.includes(genre)
                              ? "bg-green-700 hover:bg-red-700"
                              : "bg-gray-700 hover:bg-green-700"
                          } transition-all text-white text-2xl p-2`}
                        >
                          {genre}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Genre */}

                {/* Event image */}
                <div className="flex flex-col justify-start gap-1 mt-6">
                  <label className="mb-0 text-neutral-500 text-xl">
                    Event image (ignore that it doesnt show the title)
                  </label>
                  <input
                    type="file"
                    accept=".jpeg, .jpg, .png"
                    className={`dark-input ${
                      error.includes("event_image_filename")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    onChange={(e) => {
                      validateAndUploadImage(e);
                    }}
                  />
                  {event.event_image_filename && (
                    <img
                      src={getPublicImageUrl(event.event_image_filename)}
                      className="w-[200px] h-auto"
                      alt={""}
                    />
                  )}
                </div>

                {error.length > 0 && (
                  <p className="mt-5 font-semibold text-red-500 text-3xl">
                    Please fill in all fields correctly.
                  </p>
                )}

                <div className="flex justify-end items-center max-lg:mb-[50px] pt-9 w-full">
                  <button
                    onClick={() => {
                      saveEvent();
                    }}
                    className="bg-[#07af71] hover:bg-[#38c28f] px-6 lg:px-12 py-3 rounded-lg font-semibold text-white text-3xl transition-all"
                  >
                    Save event
                  </button>
                </div>
              </div>
            </div>
          )}
          {activeTab === "spinwin" && (
            <div className="mt-12">
              <SpinWinTab event={event} refreshEvent={fetchEvent} />
            </div>
          )}
          {activeTab === "attendees" && (
            <div className="mt-12">
              <Table className="bg-black mt-8">
                <Table.Head>
                  <Table.HeadCell className="text-3xl capitalize">
                    Date of signup
                  </Table.HeadCell>
                  <Table.HeadCell className="text-3xl capitalize">
                    Name
                  </Table.HeadCell>
                  <Table.HeadCell className="text-3xl capitalize">
                    Date of event
                  </Table.HeadCell>
                  <Table.HeadCell className="text-3xl capitalize">
                    Email
                  </Table.HeadCell>
                  <Table.HeadCell className="text-3xl capitalize">
                    Date of birth
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {signups?.map((signup) => {
                    return (
                      <Table.Row className="bg-white dark:bg-gray-800 dark:border-gray-700">
                        <Table.Cell className="p-8 font-medium text-gray-900 dark:text-white text-2xl whitespace-nowrap">
                          {formatDateTime(signup.created_at)}
                        </Table.Cell>
                        <Table.Cell className="p-8 font-medium text-gray-900 dark:text-white text-2xl whitespace-nowrap">
                          {`${signup.first_name} ${signup.last_name}`}
                        </Table.Cell>
                        <Table.Cell className="p-8 font-medium text-gray-900 dark:text-white text-2xl whitespace-nowrap">
                          {formatDateTime(signup.event.datetime)}
                        </Table.Cell>
                        <Table.Cell className="p-8 font-medium text-gray-900 dark:text-white text-2xl whitespace-nowrap">
                          {signup.email}
                        </Table.Cell>
                        <Table.Cell className="p-8 font-medium text-gray-900 dark:text-white text-2xl whitespace-nowrap">
                          {signup.date_of_birth}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      )}
      <SuccessPopup show={success} message="Event updated successfully" />
    </>
  );
}
